import Config from '../../../config';
let config;
import TessituraAPI from './tessitura-wrapper';
import fallbacks from 'lib/common/fallbacks';
import features from 'lib/common/features';
import logger from 'lib/common/logger';
import { enhanceSeatData, setScreenZoneData } from 'lib/common/transforms';

import get from 'lodash/get';
import has from 'lodash/has';
import isEmpty from 'lodash/isEmpty';
import isObject from 'lodash/isObject';
import filter from 'lodash/filter';
import indexOf from 'lodash/indexOf';

let API;
let INITIAL_SESSION_KEY;

export function configure(endpoint, initialSessionKey) {
  let apiInstance = new TessituraAPI(endpoint, initialSessionKey);
  API = apiInstance;

  if (initialSessionKey) {
    setInitialSessionKey(initialSessionKey);
  }
}

export function getInitialSessionKey() {
  return INITIAL_SESSION_KEY || '%sessionKey%';
}

export function setInitialSessionKey(sessionKey) {
  INITIAL_SESSION_KEY = sessionKey;
  return INITIAL_SESSION_KEY;
}

export function getSessionKey() {
  let initialSessionKey = getInitialSessionKey();
  if (initialSessionKey) {
    return Promise.resolve(initialSessionKey);
  } else {
    return API.getSessionKey();
  }
}

export function loginInfo() {
  return API.request({
    method: 'LoginInfoEx',
    params: { sessionKey: getInitialSessionKey() }
  }).then(response => {
    if (has(response, 'result.LoginInfoResults.LoginInformation')) {
      return response.result.LoginInfoResults.LoginInformation;
    } else {
      logger.captureMessage('Failed to load LoginInfoEx');
      return false;
    }
  });
}

// @TODO: Decouple this method
export function getSeatmapData(
  performanceId,
  packageNo,
  currentMOS,
  sourceCode,
  usesGeneratedSeatmap
) {
  let promises = [];

  /**
   * By default GetSeatsBriefEx requires a session key, meaning each user
   * gets a unique cache hit, so the cache only works for subsequent calls.
   *
   * If cacheableGetSeats is enabled, assuming a GetSeatsBriefExMOS method,
   * we swap out the get seats call out with one that doesn't require
   * a session key; in place we need to pass MOS and source code.
   *
   * This allows us to cache the call in a non-unique way,
   * which greatly increases the value of the cache.
   */
  if (features.isEnabled('cacheableGetSeats')) {
    promises = promises.concat([
      // Custom properties, removes need for session key
      API.requestGet({
        method: 'GetSeatsBriefWithMOS',
        params: {
          iPerformanceNumber: packageNo ? -1 : performanceId,
          iPackageNumber: packageNo ? packageNo : 0,
          cSummaryOnly: 'N',
          cCalcPackageAlloc: 'N',
          cReturnNonSeats: usesGeneratedSeatmap ? 'Y' : 'N',
          iModeOfSale: currentMOS,
          sourceCode: sourceCode
        }
      })
    ]);
  } else {
    promises = promises.concat([
      API.requestGet({
        method: 'GetSeatsBriefEx',
        params: {
          sSessionKey: getInitialSessionKey(),
          iPerformanceNumber: packageNo ? -1 : performanceId,
          iPackageNumber: packageNo ? packageNo : 0,
          cSummaryOnly: 'N',
          cCalcPackageAlloc: 'N',
          cReturnNonSeats: usesGeneratedSeatmap ? 'Y' : 'N'
        }
      })
    ]);
  }

  promises = promises.concat([
    API.requestGet({
      method: 'GetSeatStatus',
      params: {}
    })
  ]);

  if (usesGeneratedSeatmap && !packageNo) {
    promises = promises.concat([
      API.requestGet({
        method: 'GetScreens',
        params: {
          sSessionKey: getInitialSessionKey(),
          iPerformanceNumber: performanceId
        }
      }),
      API.requestGet({
        method: 'GetSeats',
        params: {
          sSessionKey: getInitialSessionKey(),
          iPerformanceNumber: performanceId,
          cSummaryOnly: 'Y',
          cCalcPackageAlloc: 'Y',
          cReturnNonSeats: 'Y'
        }
      })
    ]);
  }

  return Promise.all(promises)
    .then(function(responses) {
      let seatsBriefResults = get(
        responses[0],
        'result.GetSeatsBriefExResults'
      );
      let seatStatusResults = get(responses[1], 'result.GetSeatStatusResults');
      let screensResults = get(responses[2], 'result.GetScreensResults');
      let seatsWithZones = get(responses[3], 'result.GetSeatsResults');

      let seatsResults = enhanceSeatData(
        get(seatsBriefResults, 'S'),
        get(seatStatusResults, 'Status'),
        get(seatsBriefResults, 'Section'),
        get(screensResults, 'Screens')
      );

      let screenZoneData = seatsWithZones && setScreenZoneData(seatsWithZones);

      return {
        screensResults: screensResults,
        seatsStatusResults: seatStatusResults,
        seatsResults: seatsResults,
        screenZoneData: screenZoneData
      };
    })
    .catch(fallbacks.redirectWithError);
}

export function getMaxTicketAmount() {
  config = new Config().getConfig();
  if (config.customMaxTicketsLimit) {
    return Promise.resolve(config.customMaxTicketsLimit);
  } else {
    return API.requestGet({
      method: 'GetMaxTicketAmount',
      params: {}
    }).then(function(response) {
      let result = response.result;
      if (isObject(result)) {
        return parseInt(result[0], 10);
      } else {
        return result;
      }
    });
  }
}

export function getDetailsWithDiscounting(
  performanceId,
  packageNo,
  modeOfSale,
  sourceCode // eslint-disable-line no-unused-vars
) {
  let promise;
  if (packageNo) {
    promise = API.requestGet({
      method: 'GetPackageDetailWithDiscountingEx',
      params: {
        SessionKey: getInitialSessionKey(),
        PackageID: packageNo,
        ModeOfSale: modeOfSale
      }
    }).then(response =>
      get(response, 'result.GetPackageDetailWithDiscountingExResult')
    );
  } else {
    promise = API.requestGet({
      method: 'GetPerformanceDetailWithDiscountingEx',
      params: {
        SessionKey: '',
        iPerf_no: performanceId,
        iModeOfSale: modeOfSale,
        sContentType: ''
      }
    }).then(response =>
      get(response, 'result.GetPerformanceDetailWithDiscountingExResult')
    );
  }

  return promise.catch(fallbacks.redirectWithError);
}

export function getProductionDetail(performanceId, modeOfSale) {
  return API.requestGet({
    method: 'GetProductionDetailEx3',
    params: {
      SessionKey: getInitialSessionKey(),
      iPerf_no: performanceId,
      iModeOfSale: modeOfSale,
      sContentTypes: '[]',
      sPerformanceContentTypes: '[]'
    }
  }).then(response => get(response, 'result.GetProductionDetailEx3Result'));
}

export function getCart() {
  return API.request({
    method: 'GetCart',
    params: { sWebSessionID: getInitialSessionKey() }
  }).then(function(response) {
    if (response) {
      let result = response.result.GetCartResults;
      return !isEmpty(result) ? result : false;
    } else {
      return false;
    }
  });
}

export function ReserveTicketsSpecifiedSeats(performanceId, lineItems) {
  config = new Config().getConfig();
  let wheelChairSeats = filter(lineItems, item => {
    return (
      indexOf(
        config.specialRequests.excludeSeatType,
        parseInt(item.seatData.seat_type)
      ) > -1
    );
  });

  let leaveSingleSeats = config.specialRequests.LeaveSingleSeats
    ? config.specialRequests.LeaveSingleSeats
    : 'Y';

  if (wheelChairSeats.length) {
    leaveSingleSeats = 'Y';
  }

  return API.request({
    method: 'ReserveTicketsSpecifiedSeats',
    params: {
      sWebSessionID: getInitialSessionKey(),
      // TODO: map these before passing through
      sPriceType: lineItems.map(item => item.priceData.price_type).join(','),
      iPerformanceNumber: performanceId,
      iNumberOfSeats: lineItems.length,
      SpecialRequests: `LeaveSingleSeats=${leaveSingleSeats}`,
      RequestedSeats: lineItems.map(item => item.seatData.seat_no).join(','),
      iZone: 0
    }
  });
}

export function ReleaseTickets(iPerformanceNumber, iLineItemNumber) {
  return API.request({
    method: 'ReleaseTickets',
    params: {
      sWebSessionID: getInitialSessionKey(),
      iPerformanceNumber: iPerformanceNumber,
      iLineItemNumber: iLineItemNumber
    }
  });
}

export function RemoveNFSPackage(performanceLineItemId, performanceNumber) {
  return API.request({
    method: 'RemoveNFSPackage',
    params: {
      SessionKey: getInitialSessionKey(),
      NFSPackageLineItemId: 0,
      PerformanceLineItemId: performanceLineItemId,
      PerformanceNumber: performanceNumber
    }
  });
}

export function AddNFSPackagePerformanceItem(
  NFSPackageLineItemId,
  PackageNumber,
  PerformanceGroupNumber,
  lineItems,
  perfNo
) {
  config = new Config().getConfig();
  let priceTypes = lineItems.map(item => item.priceData.price_type).join(',');
  let requestedSeats = lineItems.map(item => item.seatData.seat_no).join(',');

  return API.request({
    method: 'AddNFSPackagePerformanceItem',
    params: {
      SessionKey: getInitialSessionKey(),
      NFSPackageLineItemId: NFSPackageLineItemId || 0,
      PriceType: priceTypes,
      PackageNumber: PackageNumber,
      PerformanceGroupNumber: PerformanceGroupNumber,
      PerformanceNumber: perfNo,
      NumberOfSeats: lineItems.length,
      Zone: 0,
      RequestedSeats: requestedSeats,
      LeaveSingleSeats:
        config.specialRequests.LeaveSingleSeats === 'Y' ? `true` : 'false',
      SpecialRequests: '',
      UnSeated: 'false'
    }
  });
}

export function AddPackageItemSeated(currentFixedPackage, lineItems) {
  config = new Config().getConfig();
  return API.request({
    method: 'AddPackageItemSeated',
    params: {
      SessionKey: getInitialSessionKey(),
      PriceType: lineItems.map(item => item.priceData.price_type).join(','),
      PackageNumber: currentFixedPackage.pkg_no,
      NumberOfSeats: lineItems.length,
      Zone: currentFixedPackage.zone_no,
      RequestedSeats: lineItems.map(item => item.seatData.seat_no).join(','),
      LeaveSingleSeats:
        config.specialRequests.LeaveSingleSeats === 'Y' ? `true` : 'false'
    }
  });
}

export function RemovePackageItem(pkgNo, liSeqNo) {
  return API.request({
    method: 'RemovePackageItem',
    params: {
      SessionKey: getInitialSessionKey(),
      PackageNumber: pkgNo,
      PackageLineItemID: liSeqNo
    }
  });
}

/**
 * Custom Method (GetCurrentPromoCode)
 */
export function GetCurrentPromoCode() {
  return API.requestGet({
    method: 'GetCurrentPromoCode',
    params: {
      SessionKey: getInitialSessionKey()
    }
  });
}

/**
 * Custom Method (SeatUpsells)
 */
export function SeatUpsells() {
  return API.requestGet({
    method: 'SeatUpsells',
    params: {}
  }).then(function(response) {
    let result = get(response, 'result.GetLocalDataResults.LocalData');
    return result;
  });
}

export default {
  configure,
  getInitialSessionKey,
  setInitialSessionKey,
  getSessionKey,
  loginInfo,
  getSeatmapData,
  getMaxTicketAmount,
  getDetailsWithDiscounting,
  getProductionDetail,
  getCart,
  ReserveTicketsSpecifiedSeats,
  ReleaseTickets,
  RemoveNFSPackage,
  AddNFSPackagePerformanceItem,
  AddPackageItemSeated,
  RemovePackageItem,
  GetCurrentPromoCode,
  SeatUpsells
};



// WEBPACK FOOTER //
// ./src/javascripts/lib/tessitura/SOAP/API.js